import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import SEO from '../../components/SEO';
import FAQ from '../../components/ui/FAQ';
import { aiChatbotsFAQs } from '../../data/faqs';
import { sendRequirementsFormEmail } from '../../utils/emailService';
import { scrollToElement } from '../../utils/scrollUtils';
import { useContactInfo } from '../../utils/contactObfuscation';

const AIChatbotsLanding = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: 'AI Chatbots',
    requirements: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const contactInfo = useContactInfo();

  const seoData = {
    title: 'AI Chatbots & Virtual Assistants - Conversational AI | OctalChip',
    description: 'Intelligent conversational AI solutions for customer support, sales, and engagement. Build chatbots that understand context and provide human-like interactions.',
    keywords: 'AI chatbots, virtual assistants, conversational AI, chatbot development, customer support bots, NLP chatbots',
    canonical: 'https://www.octalchip.com/landing/ai-chatbots',
    faqData: aiChatbotsFAQs,
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "AI Chatbots & Virtual Assistants",
      "description": "Intelligent conversational AI solutions for customer support and engagement",
      "provider": {
        "@type": "Organization",
        "name": "OctalChip",
        "url": "https://www.octalchip.com"
      }
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const result = await sendRequirementsFormEmail(formData);
      
      if (result.success) {
        setSubmitMessage('Thank you! Your requirements have been submitted successfully. We\'ll get back to you within 24 hours with a detailed quote.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          projectType: 'AI Chatbots',
          requirements: ''
        });
      } else {
        setSubmitMessage('Sorry, there was an error submitting your requirements. Please try again or contact us directly.');
      }
    } catch (error) {
      setSubmitMessage('Sorry, there was an error submitting your requirements. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    {
      icon: 'MessageCircle',
      title: 'Conversational AI',
      description: 'Natural language understanding for human-like conversations'
    },
    {
      icon: 'Globe',
      title: 'Multi-language Support',
      description: 'Chatbots that communicate in multiple languages'
    },
    {
      icon: 'Brain',
      title: 'Context Understanding',
      description: 'Maintain conversation context for better interactions'
    },
    {
      icon: 'Clock',
      title: '24/7 Availability',
      description: 'Round-the-clock customer support and assistance'
    }
  ];

  const technologies = [
    { name: 'OpenAI GPT', description: 'Advanced language models' },
    { name: 'Dialogflow', description: 'Google\'s conversational AI' },
    { name: 'Rasa', description: 'Open-source conversational AI' },
    { name: 'Microsoft Bot', description: 'Bot development framework' },
    { name: 'Watson Assistant', description: 'IBM\'s AI assistant' },
    { name: 'Custom NLP', description: 'Tailored NLP solutions' }
  ];

  const useCases = [
    {
      title: 'Customer Support',
      description: 'Handle customer queries and provide instant responses',
      icon: 'Headphones'
    },
    {
      title: 'Sales Assistance',
      description: 'Guide customers through product selection and purchase',
      icon: 'ShoppingCart'
    },
    {
      title: 'Lead Qualification',
      description: 'Qualify leads and route them to appropriate teams',
      icon: 'Users'
    },
    {
      title: 'FAQ Automation',
      description: 'Answer frequently asked questions automatically',
      icon: 'HelpCircle'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={seoData.canonical}
        faqData={seoData.faqData}
        structuredData={seoData.structuredData}
      />
      <Header />
      
      {/* Hero Section */}
      <section id="hero" className="py-12 sm:py-16 md:py-20 lg:py-24 bg-gradient-to-br from-purple-50 via-white to-indigo-50">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div className="text-center lg:text-left">
                <div className="inline-block bg-[#00d690]/10 text-[#00d690] px-4 py-2 rounded-full text-sm font-medium mb-6">
                  AI Chatbots
                </div>
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight text-gray-800">
                  World's Best AI Chatbot Services - Build Intelligent Bots Globally
                </h1>
                <p className="text-lg md:text-xl mb-8 text-gray-600 leading-relaxed">
                  Trusted by businesses worldwide, we create AI-powered chatbots and virtual assistants that understand context, provide natural responses, and deliver exceptional customer experiences. Get conversational AI that handles support, sales, and engagement - recognized globally for excellence.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8">
                  <Button
                    variant="primary"
                    animation="glow"
                    className="px-8 py-4 text-base md:text-lg bg-[#00d690] text-white hover:bg-[#00b876] shadow-lg"
                    onClick={async () => {
                      await scrollToElement('#get-quote', { 
                        smooth: true,
                        maxRetries: 15,
                        retryDelay: 150
                      }).catch(() => {});
                    }}
                  >
                    Get Free Quote
                  </Button>
                  <Button
                    variant="outline"
                    animation="rotate"
                    className="px-8 py-4 text-base md:text-lg border-[#00d690] text-[#00d690] hover:bg-[#00d690] hover:text-white"
                    onClick={() => navigate('/services/ai-ml-services/ai-chatbots')}
                  >
                    View Full Service
                  </Button>
                </div>

                <div className="flex flex-wrap gap-6 text-sm justify-center lg:justify-start">
                  <div className="flex items-center">
                    <Icon name="Check" size={20} className="text-[#00d690] mr-2" />
                    <span className="text-gray-700">Natural Language</span>
                  </div>
                  <div className="flex items-center">
                    <Icon name="Check" size={20} className="text-[#00d690] mr-2" />
                    <span className="text-gray-700">Context Aware</span>
                  </div>
                  <div className="flex items-center">
                    <Icon name="Check" size={20} className="text-[#00d690] mr-2" />
                    <span className="text-gray-700">Multi-language</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-xl border border-gray-100">
                <div className="text-center">
                  <div className="w-16 h-16 bg-[#00d690]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="MessageSquare" size={32} className="text-[#00d690]" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-gray-800">Get Your Free Quote</h3>
                  <p className="text-gray-600 mb-6">Tell us about your chatbot project and get a detailed estimate in 24 hours</p>
                  
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <input 
                      type="text" 
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                      placeholder="Your Name"
                      required
                    />
                    <input 
                      type="email" 
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                      placeholder="Your Email"
                      required
                    />
                    <input 
                      type="tel" 
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                      placeholder="Your Phone"
                      required
                    />
                    <textarea 
                      name="requirements"
                      value={formData.requirements}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                      placeholder="Describe your chatbot project..."
                      required
                    ></textarea>
                    
                    {submitMessage && (
                      <div className={`p-4 rounded-lg text-center ${
                        submitMessage.includes('Thank you') 
                          ? 'bg-green-100 text-green-700 border border-green-200' 
                          : 'bg-[#00d690]/10 text-red-700 border border-red-200'
                      }`}>
                        {submitMessage}
                      </div>
                    )}
                    
                    <Button 
                      type="submit"
                      variant="primary"
                      animation="shake"
                      className="w-full py-3 bg-[#00d690] text-white hover:bg-[#00b876] shadow-lg"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Submitting...' : 'Get Free Quote'}
                    </Button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            <div className="text-center">
              <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#00d690] mb-2">20+</div>
              <div className="text-sm md:text-base text-gray-600">Chatbot Projects</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#00d690] mb-2">70%</div>
              <div className="text-sm md:text-base text-gray-600">Inquiry Automation</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#00d690] mb-2">24/7</div>
              <div className="text-sm md:text-base text-gray-600">Availability</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#00d690] mb-2">95%</div>
              <div className="text-sm md:text-base text-gray-600">Satisfaction Rate</div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">Why Choose Our AI Chatbot Services?</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              We deliver chatbots that provide intelligent, context-aware conversations
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center p-6 bg-gray-50 rounded-xl hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 bg-[#00d690]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name={benefit.icon} size={32} className="text-[#00d690]" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section id="technologies" className="py-12 sm:py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">Our Chatbot Technology Stack</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              We use industry-leading conversational AI platforms and frameworks
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {technologies.map((tech, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow text-center">
                <h3 className="font-bold text-gray-800 mb-2">{tech.name}</h3>
                <p className="text-sm text-gray-600">{tech.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section id="use-cases" className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">What We Build</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              From customer support to sales assistance, we build chatbots for every business need
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {useCases.map((useCase, index) => (
              <div key={index} className="bg-gray-50 p-6 md:p-8 rounded-xl hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-4">
                  <Icon name={useCase.icon} size={24} className="text-[#00d690]" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">{useCase.title}</h3>
                <p className="text-gray-600">{useCase.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      <section id="pain-points" className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">Common AI Chatbot Challenges We Solve</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              We understand the pain points businesses face. Here's how we help solve them
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto">
            <div className="bg-green-50 border-l-4 border-[#00d690] p-6 rounded-xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertCircle" size={24} className="text-[#00d690]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">High Support Costs</h3>
                  <p className="text-gray-600">24/7 customer support requires large teams and high costs. We build AI chatbots handling 70-80% of inquiries automatically reducing support costs by 60-70%.</p>
                </div>
              </div>
            </div>
            <div className="bg-green-50 border-l-4 border-[#00d690] p-6 rounded-xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertCircle" size={24} className="text-[#00d690]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Slow Response Times</h3>
                  <p className="text-gray-600">Customers wait hours for responses leading to frustration. We build chatbots responding instantly 24/7 improving customer satisfaction and reducing wait times to zero.</p>
                </div>
              </div>
            </div>
            <div className="bg-green-50 border-l-4 border-[#00d690] p-6 rounded-xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertCircle" size={24} className="text-[#00d690]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Poor Context Understanding</h3>
                  <p className="text-gray-600">Simple chatbots don't understand context leading to wrong answers. We build intelligent chatbots with context awareness and memory providing accurate, relevant responses.</p>
                </div>
              </div>
            </div>
            <div className="bg-green-50 border-l-4 border-[#00d690] p-6 rounded-xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertCircle" size={24} className="text-[#00d690]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Limited Language Support</h3>
                  <p className="text-gray-600">Single-language chatbots limit global reach. We build multilingual chatbots supporting 50+ languages enabling global customer engagement and support.</p>
                </div>
              </div>
            </div>
            <div className="bg-green-50 border-l-4 border-[#00d690] p-6 rounded-xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertCircle" size={24} className="text-[#00d690]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Integration Complexity</h3>
                  <p className="text-gray-600">Chatbots that don't integrate with systems provide limited value. We integrate chatbots with CRM, databases, and APIs providing complete business functionality.</p>
                </div>
              </div>
            </div>
            <div className="bg-green-50 border-l-4 border-[#00d690] p-6 rounded-xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertCircle" size={24} className="text-[#00d690]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Conversation Quality Issues</h3>
                  <p className="text-gray-600">Robotic responses frustrate users and reduce engagement. We build natural, context-aware chatbots with personality and empathy providing human-like conversations that users love.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="py-12 sm:py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">Our Chatbot Development Process</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              A proven methodology that ensures quality, transparency, and on-time delivery
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            <div className="bg-white p-6 md:p-8 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-[#00d690] text-white rounded-full flex items-center justify-center font-bold text-lg mb-4">
                01
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Requirements & Use Cases</h3>
              <p className="text-gray-600">Define chatbot goals and conversation flows</p>
            </div>
            <div className="bg-white p-6 md:p-8 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-[#00d690] text-white rounded-full flex items-center justify-center font-bold text-lg mb-4">
                02
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Design & Training</h3>
              <p className="text-gray-600">Design conversations and train the AI model</p>
            </div>
            <div className="bg-white p-6 md:p-8 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-[#00d690] text-white rounded-full flex items-center justify-center font-bold text-lg mb-4">
                03
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Development & Integration</h3>
              <p className="text-gray-600">Build chatbot and integrate with your systems</p>
            </div>
            <div className="bg-white p-6 md:p-8 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-[#00d690] text-white rounded-full flex items-center justify-center font-bold text-lg mb-4">
                04
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Testing & Deployment</h3>
              <p className="text-gray-600">Test conversations and deploy to production</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">Client Success Stories</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-5xl mx-auto">
            <div className="bg-gray-50 p-6 md:p-8 rounded-xl">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Icon key={i} name="Star" size={20} className="text-yellow-400 mr-1" />
                ))}
              </div>
              <p className="text-gray-600 mb-4 italic leading-relaxed">"OctalChip's AI chatbot transformed our customer support. We automated 70% of inquiries with their intelligent solution. Highly professional team."</p>
              <div>
                <p className="font-semibold text-gray-800">Michael Chen</p>
                <p className="text-sm text-gray-500">DataDriven Inc</p>
              </div>
            </div>
            <div className="bg-gray-50 p-6 md:p-8 rounded-xl">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Icon key={i} name="Star" size={20} className="text-yellow-400 mr-1" />
                ))}
              </div>
              <p className="text-gray-600 mb-4 italic leading-relaxed">"The chatbot they built handles customer queries 24/7. Response time improved by 90% and customer satisfaction increased significantly."</p>
              <div>
                <p className="font-semibold text-gray-800">Sarah Johnson</p>
                <p className="text-sm text-gray-500">TechFlow Solutions</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">Our AI Chatbot Portfolio</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              See how we've helped businesses worldwide build successful chatbot solutions
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            <div className="bg-gray-50 p-6 md:p-8 rounded-xl hover:shadow-lg transition-shadow border border-gray-200">
              <div className="w-16 h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-4">
                <Icon name="MessageCircle" size={32} className="text-[#00d690]" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Customer Support Chatbot</h3>
              <p className="text-gray-600 mb-4">Built intelligent support chatbot handling 10,000+ inquiries daily with 85% resolution rate. Reduced support costs by 70% and improved response time to under 2 seconds.</p>
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">OpenAI</span>
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">NLP</span>
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">24/7</span>
              </div>
              <p className="text-sm text-gray-500">Client: TechFlow Solutions | Location: USA</p>
            </div>
            <div className="bg-gray-50 p-6 md:p-8 rounded-xl hover:shadow-lg transition-shadow border border-gray-200">
              <div className="w-16 h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-4">
                <Icon name="ShoppingCart" size={32} className="text-[#00d690]" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">E-commerce Sales Bot</h3>
              <p className="text-gray-600 mb-4">Developed sales chatbot for e-commerce platform providing product recommendations and handling purchases. Increased conversion rate by 35% and average order value by 25%.</p>
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">Dialogflow</span>
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">Recommendation</span>
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">Payment</span>
              </div>
              <p className="text-sm text-gray-500">Client: E-commerce Plus | Location: UK</p>
            </div>
            <div className="bg-gray-50 p-6 md:p-8 rounded-xl hover:shadow-lg transition-shadow border border-gray-200">
              <div className="w-16 h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-4">
                <Icon name="Users" size={32} className="text-[#00d690]" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Healthcare Virtual Assistant</h3>
              <p className="text-gray-600 mb-4">Created HIPAA-compliant healthcare chatbot for appointment scheduling and patient inquiries. Handles 5,000+ interactions daily improving patient engagement by 200%.</p>
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">HIPAA</span>
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">Multi-language</span>
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">Secure</span>
              </div>
              <p className="text-sm text-gray-500">Client: HealthTech Solutions | Location: Canada</p>
            </div>
          </div>
        </div>
      </section>

      {/* Special Offers Section */}
      <section id="offers" className="py-12 sm:py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">Special Offers & Free Trials</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              Start your chatbot project with confidence - try our services risk-free
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-200 relative overflow-hidden">
              <div className="absolute top-4 right-4">
                <span className="bg-[#00d690] text-white px-3 py-1 rounded-full text-sm font-medium">POPULAR</span>
              </div>
              <div className="w-16 h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-6">
                <Icon name="Play" size={32} className="text-[#00d690]" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-800">Free Trial</h3>
              <p className="text-gray-600 mb-6">Get 2 hours of free chatbot development work to experience our quality</p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>2 hours of free development</span></li>
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>No commitment required</span></li>
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>Keep the chatbot you receive</span></li>
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>Full project consultation</span></li>
              </ul>
              <Button variant="primary" animation="bounce" className="w-full bg-[#00d690] text-white hover:bg-[#00b876]" onClick={async () => { await scrollToElement('#get-quote', { smooth: true, maxRetries: 15, retryDelay: 150 }).catch(() => {}); }}>Start Free Trial</Button>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-200 relative overflow-hidden">
              <div className="absolute top-4 right-4">
                <span className="bg-[#00d690] text-white px-3 py-1 rounded-full text-sm font-medium">NEW</span>
              </div>
              <div className="w-16 h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-6">
                <Icon name="Gift" size={32} className="text-[#00d690]" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-800">Free Credits</h3>
              <p className="text-gray-600 mb-6">Get $500 in chatbot development credits when you sign up</p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>$500 free credits</span></li>
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>Valid for 30 days</span></li>
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>Deducted from final invoice</span></li>
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>Use on any project</span></li>
              </ul>
              <Button variant="primary" animation="pulse" className="w-full bg-[#00d690] text-white hover:bg-[#00b876]" onClick={async () => { await scrollToElement('#get-quote', { smooth: true, maxRetries: 15, retryDelay: 150 }).catch(() => {}); }}>Claim Free Credits</Button>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-200 relative overflow-hidden">
              <div className="absolute top-4 right-4">
                <span className="bg-[#00d690] text-white px-3 py-1 rounded-full text-sm font-medium">GUARANTEE</span>
              </div>
              <div className="w-16 h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-6">
                <Icon name="Shield" size={32} className="text-[#00d690]" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-800">100% Satisfaction</h3>
              <p className="text-gray-600 mb-6">Not satisfied? Get your money back within 7 days</p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>7-day money-back guarantee</span></li>
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>No questions asked</span></li>
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>Keep all deliverables</span></li>
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>Full refund guarantee</span></li>
              </ul>
              <Button variant="primary" animation="scale" className="w-full bg-[#00d690] text-white hover:bg-[#00b876]" onClick={async () => { await scrollToElement('#get-quote', { smooth: true, maxRetries: 15, retryDelay: 150 }).catch(() => {}); }}>Start Risk-Free</Button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators Section */}
      <section id="trust" className="py-12 bg-white border-t border-gray-200">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="flex flex-wrap justify-center items-center gap-8 sm:gap-12">
            <div className="flex items-center gap-3">
              <Icon name="Shield" size={24} className="text-[#00d690]" />
              <span className="text-sm sm:text-base font-medium text-gray-700">ISO Certified</span>
            </div>
            <div className="flex items-center gap-3">
              <Icon name="Lock" size={24} className="text-[#00d690]" />
              <span className="text-sm sm:text-base font-medium text-gray-700">GDPR Compliant</span>
            </div>
            <div className="flex items-center gap-3">
              <Icon name="FileText" size={24} className="text-[#00d690]" />
              <span className="text-sm sm:text-base font-medium text-gray-700">NDA Protected</span>
            </div>
            <div className="flex items-center gap-3">
              <Icon name="CheckCircle" size={24} className="text-[#00d690]" />
              <span className="text-sm sm:text-base font-medium text-gray-700">Money-Back Guarantee</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="get-quote" className="py-12 sm:py-16 md:py-20 bg-gradient-to-br from-[#f4faf8] via-white to-[#e8f5e8]">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-block bg-[#00d690]/10 text-[#00d690] px-4 py-2 rounded-full text-sm font-medium mb-6">
              Start Your Chatbot Project Today
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 text-gray-800">Ready to Build Your AI Chatbot?</h2>
            <p className="text-lg md:text-xl mb-8 text-gray-600">
              Get a free consultation and detailed quote for your AI chatbot project.
            </p>
            
            <div className="bg-white rounded-2xl p-6 md:p-8 max-w-2xl mx-auto shadow-xl border border-gray-100">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input 
                    type="text" 
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                    placeholder="Your Name"
                    required
                  />
                  <input 
                    type="email" 
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                    placeholder="Your Email"
                    required
                  />
                </div>
                <input 
                  type="tel" 
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                  placeholder="Your Phone"
                  required
                />
                <textarea 
                  name="requirements"
                  value={formData.requirements}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                  placeholder="Describe your AI chatbot requirements..."
                  required
                ></textarea>
                
                {submitMessage && (
                  <div className={`p-4 rounded-lg text-center ${
                    submitMessage.includes('Thank you') 
                      ? 'bg-green-100 text-green-700 border border-green-200' 
                      : 'bg-[#00d690]/10 text-red-700 border border-red-200'
                  }`}>
                    {submitMessage}
                  </div>
                )}
                
                <Button 
                  type="submit"
                  variant="primary"
                  animation="pulse"
                  className="w-full py-3 bg-[#00d690] text-white hover:bg-[#00b876] shadow-lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Get Free Quote & Consultation'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQ 
        faqs={aiChatbotsFAQs}
        title="Frequently Asked Questions"
        description="Common questions about our AI chatbot services."
      />

      <Footer />
    </div>
  );
};

export default AIChatbotsLanding;

